import {
  MoreVertical,
  Play,
  Edit,
  Copy,
  Trash2,
  Rocket,
  CheckCircle,
  Clock,
  Archive,
  TestTube,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Link } from 'react-router-dom'
import { type CustomAgentSummary, type DeploymentStatus } from '@/lib/api/services/aiStudio'
import { formatDistanceToNow } from 'date-fns'
import { cn } from '@/lib/utils'

export type SortField = 'name' | 'agentType' | 'deploymentStatus' | 'skillCount' | 'createdAt'
export type SortOrder = 'asc' | 'desc'

interface AgentsTableViewProps {
  agents: CustomAgentSummary[]
  sortField: SortField
  sortOrder: SortOrder
  onSort: (field: SortField) => void
  onTest: (agent: CustomAgentSummary) => void
  onDeploy: (agent: CustomAgentSummary) => void
  onDelete: (agent: CustomAgentSummary) => void
}

// MS-style agent colors - matches AgentSeeder configuration
const agentConfig: Record<string, { color: string }> = {
  alex: { color: '#3B82F6' },
  scarlet: { color: '#EF4444' },
  sia: { color: '#8B5CF6' },
  olga: { color: '#F59E0B' },
  dimi: { color: '#F97316' },
  andrew: { color: '#14B8A6' },
}

// Generate 2-letter abbreviation from name
function getAbbreviation(name: string): string {
  if (!name || name.length === 0) return 'AI'
  const words = name.trim().split(/\s+/).filter(w => w.length > 0)
  if (words.length >= 2 && words[0] && words[1]) {
    return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase()
  }
  return name.slice(0, 2).toUpperCase()
}

// Get color for agent
function getAgentColor(slug: string, isCustom: boolean): string {
  if (!isCustom && agentConfig[slug]) {
    return agentConfig[slug].color
  }
  const colors = ['#6366F1', '#EC4899', '#10B981', '#F59E0B', '#EF4444', '#06B6D4', '#8B5CF6'] as const
  let hash = 0
  for (let i = 0; i < slug.length; i++) {
    hash = slug.charCodeAt(i) + ((hash << 5) - hash)
  }
  return colors[Math.abs(hash) % colors.length] ?? '#6366F1'
}

const deploymentStatusConfig: Record<DeploymentStatus, { label: string; color: string; icon: React.ElementType }> = {
  Draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300', icon: Clock },
  Testing: { label: 'Testing', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: TestTube },
  Active: { label: 'Active', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', icon: CheckCircle },
  Deprecated: { label: 'Deprecated', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: Archive },
}

interface SortableHeaderProps {
  field: SortField
  currentField: SortField
  currentOrder: SortOrder
  onSort: (field: SortField) => void
  children: React.ReactNode
  className?: string
}

function SortableHeader({ field, currentField, currentOrder, onSort, children, className }: SortableHeaderProps) {
  const isActive = currentField === field
  return (
    <TableHead className={className}>
      <button
        onClick={() => onSort(field)}
        className={cn(
          "flex items-center gap-1 hover:text-gray-900 dark:hover:text-white transition-colors -ml-2 px-2 py-1 rounded",
          isActive && "text-gray-900 dark:text-white font-medium"
        )}
      >
        {children}
        {isActive ? (
          currentOrder === 'asc' ? (
            <ArrowUp className="h-3.5 w-3.5" />
          ) : (
            <ArrowDown className="h-3.5 w-3.5" />
          )
        ) : (
          <ArrowUpDown className="h-3.5 w-3.5 opacity-50" />
        )}
      </button>
    </TableHead>
  )
}

export function AgentsTableView({ agents, sortField, sortOrder, onSort, onTest, onDeploy, onDelete }: AgentsTableViewProps) {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-50 dark:bg-gray-800/50">
            <SortableHeader field="name" currentField={sortField} currentOrder={sortOrder} onSort={onSort} className="w-[180px]">
              Agent
            </SortableHeader>
            <SortableHeader field="agentType" currentField={sortField} currentOrder={sortOrder} onSort={onSort} className="w-[140px]">
              Type
            </SortableHeader>
            <SortableHeader field="deploymentStatus" currentField={sortField} currentOrder={sortOrder} onSort={onSort} className="w-[100px]">
              Status
            </SortableHeader>
            <SortableHeader field="skillCount" currentField={sortField} currentOrder={sortOrder} onSort={onSort} className="w-[80px] text-center">
              Skills
            </SortableHeader>
            <SortableHeader field="createdAt" currentField={sortField} currentOrder={sortOrder} onSort={onSort} className="w-[120px]">
              Created
            </SortableHeader>
            <TableHead className="w-[80px] text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {agents.map((agent) => {
            const statusConfig = deploymentStatusConfig[agent.deploymentStatus]
            const StatusIcon = statusConfig.icon
            const agentColor = getAgentColor(agent.slug, agent.isCustom)
            const abbreviation = getAbbreviation(agent.name)

            return (
              <TableRow
                key={agent.id}
                className="hover:bg-gray-50 dark:hover:bg-gray-800/50"
              >
                <TableCell className="py-2">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-8 h-8 rounded-md flex items-center justify-center text-white font-medium text-xs shrink-0"
                      style={{ backgroundColor: agentColor }}
                    >
                      {abbreviation}
                    </div>
                    <span className="font-medium text-gray-900 dark:text-white truncate text-sm">
                      {agent.name}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="py-2">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-600 dark:text-gray-300">
                      {agent.agentType}
                    </span>
                    {agent.isCustom ? (
                      <Badge className="rounded px-1.5 py-0 text-[10px] font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                        Custom
                      </Badge>
                    ) : (
                      <Badge className="rounded px-1.5 py-0 text-[10px] font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400">
                        Preset
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="py-2">
                  <Badge className={`${statusConfig.color} rounded px-1.5 py-0 text-[10px] font-medium`}>
                    <StatusIcon className="h-2.5 w-2.5 mr-0.5" />
                    {statusConfig.label}
                  </Badge>
                </TableCell>
                <TableCell className="text-center py-2">
                  <span className="text-xs text-gray-600 dark:text-gray-300">
                    {agent.skillCount || '-'}
                  </span>
                </TableCell>
                <TableCell className="text-xs text-gray-500 dark:text-gray-400 py-2">
                  {formatDistanceToNow(new Date(agent.createdAt), { addSuffix: true })}
                </TableCell>
                <TableCell className="text-right py-2">
                  <div className="flex items-center justify-end gap-0.5">
                    <Link to={`/ai-studio/chat/${agent.slug}`}>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20"
                        title="Chat with this agent"
                      >
                        <Play className="h-3.5 w-3.5" />
                      </Button>
                    </Link>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                          <MoreVertical className="h-3.5 w-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44">
                        <DropdownMenuItem asChild>
                          <Link to={`/ai-studio/builder/${agent.id}`}>
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onTest(agent)}>
                          <TestTube className="mr-2 h-4 w-4" />
                          Run Tests
                        </DropdownMenuItem>
                        {agent.deploymentStatus !== 'Active' && (
                          <DropdownMenuItem onClick={() => onDeploy(agent)}>
                            <Rocket className="mr-2 h-4 w-4" />
                            Deploy
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem>
                          <Copy className="mr-2 h-4 w-4" />
                          Clone
                        </DropdownMenuItem>
                        {agent.isCustom && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => onDelete(agent)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
